import { type CountryCode, countries, normalisePhone } from "@rhema/client";
import { cn, focusRing, Select, type SelectOption } from "@rhema/ui";
import { useId, useMemo } from "react";

export type PhoneValue = { region: CountryCode; number: string };

/** The E.164 form of what was typed, or null when it cannot be a phone number. */
export function phoneOf(value: PhoneValue): string | null {
  return normalisePhone(value.number, value.region);
}

/** A country picker and a number field, together as one phone number. */
export function PhoneField({
  label,
  value,
  onChange,
  autoFocus,
  disabled,
  error,
}: {
  label: string;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string | null;
}) {
  const id = useId();
  const options = useMemo<SelectOption[]>(
    () =>
      countries(globalThis.navigator?.language || "en").map((c) => ({
        value: c.code,
        label: c.name,
        detail: `+${c.callingCode}`,
        short: `${c.code} +${c.callingCode}`,
        keywords: `${c.code} ${c.callingCode}`,
      })),
    [],
  );
  const errorId = `${id}-error`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold text-ink">
        {label}
      </label>
      <div className="flex gap-2">
        <Select
          label="Country"
          hideLabel
          searchable
          searchPlaceholder="Search countries"
          value={value.region}
          options={options}
          disabled={disabled}
          onChange={(region) => onChange({ ...value, region: region as CountryCode })}
          className="h-12 w-[112px] flex-none"
          menuClassName="w-[300px]"
        />
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          // biome-ignore lint/a11y/noAutofocus: only forms that open on their own focus the number
          autoFocus={autoFocus}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          placeholder="Phone number"
          value={value.number}
          onChange={(e) => onChange({ ...value, number: e.target.value })}
          className={cn(
            "h-12 min-w-0 flex-1 rounded-field border bg-surface px-4 font-sans text-[15px] text-ink placeholder:text-ink-meta focus:border-blue",
            error ? "border-scarlet" : "border-line",
            focusRing,
          )}
        />
      </div>
      {error && (
        <span id={errorId} className="text-[13px] font-semibold text-scarlet">
          {error}
        </span>
      )}
    </div>
  );
}
